import {Component, Input, OnInit} from '@angular/core';
import {
  CategoryItem,
  CompetitorRoundResultWarning,
  Gender,
  League,
  LeagueCompetitorRoundResult,
  LeaguePoints,
  LeagueRoundResultContext,
  LeagueRoundResultMeta,
  LeagueRoundResultsFilter,
  LeagueStandingsRace,
  PagedFilter,
  Pages,
  PublicInclusionStatus,
  RoundCountStatus,
  TeamComposition,
  TOAST_STATE
} from "@model";
import {ConfirmationModalComponent, FilterGroup, FilterGroupType, FilterItem} from "@components";
import {Observable, Subject} from "rxjs";
import {TranslocoService} from "@ngneat/transloco";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {LeagueResultsService, ToastService} from "@services";
import {FormControl, FormGroup} from "@angular/forms";
import {switchMap} from "rxjs/operators";
import {getMultiLanguageValue, localizedDateString} from "@utility";
import {PagingComponent} from "../../../../../components/paging.component";

@Component({
  selector: 'app-league-round-results',
  templateUrl: './league-round-results.component.html',
  styleUrl: './league-round-results.component.scss',
  standalone: false,
})
export class LeagueRoundResultsComponent extends PagingComponent<LeagueCompetitorRoundResult> implements OnInit{
  @Input({required: true}) league: League | undefined;
  @Input() categoryItems: CategoryItem[] = [];
  @Input() availableRaces: LeagueStandingsRace[] = [];

  eventMap = new Map<number, { label: string; raceIds: (number | undefined)[]; raceLabels: string[] }>();
  eventIds: number[] = [];
  eventListLabels: string[] = [];
  isTeam: boolean = false;
  leagueId: number | undefined;

  currentRoundResultsMeta: LeagueRoundResultMeta | undefined;

  filterGroups: FilterGroup[] = [];
  filter: LeagueRoundResultsFilter = new LeagueRoundResultsFilter();
  results: LeagueCompetitorRoundResult[] = [];
  originalResults: Map<number, LeagueCompetitorRoundResult> = new Map();
  rankingContext: LeagueRoundResultContext = LeagueRoundResultContext.RACE;
  componentType: LeagueRoundResultsComponentType = LeagueRoundResultsComponentType.VIEW;
  compositionMap: Map<number, TeamComposition> = new Map();

  editEnabled: boolean = false;

  roundResultsContextOptions = [
    {
      value: LeagueRoundResultContext.ROUND,
      label: 'league.results.context.ROUND'
    },
    {
      value: LeagueRoundResultContext.RACE,
      label: 'league.results.context.RACE'
    }
  ];

  private metaSubject = new Subject<number>();

  constructor(public translocoService: TranslocoService,
              private modalService: NgbModal,
              private leagueResultsService: LeagueResultsService,
              private toastService: ToastService) {
    super();
  }

  inputForm = new FormGroup({
    eventId: new FormControl(),
    raceId: new FormControl(),
  });

  override ngOnInit() {
    super.ngOnInit();
    this.leagueId = this.league?.id;
    this.buildEventSelectionList();
    this.registerSubscriptions();
    this.setInitialEvent();
  }

  private buildEventSelectionList(): void {
    const roundLabel = this.translocoService.translate('event.item.round')?.[0]?.toUpperCase() ?? '';
    const lang = this.translocoService.getActiveLang();

    this.eventMap.clear();
    this.availableRaces?.forEach(r => {
      const eventId = r?.eventId;
      if (eventId == null) return;

      if (!this.eventMap.has(eventId)) {
        this.eventMap.set(eventId, {
          label: `${roundLabel}${r?.round ?? ''} - ${getMultiLanguageValue(r?.eventName, lang) ?? '-'} - ${localizedDateString(r?.eventStart, 'short', this.translocoService.getActiveLang())}`,
          raceIds: [],
          raceLabels: [],
        });
      }
      const entry = this.eventMap.get(eventId)!;
      entry.raceIds.push(r?.raceId);
      entry.raceLabels.push(getMultiLanguageValue(r?.raceName, lang) ?? '-');
    });

    this.eventIds = Array.from(this.eventMap.keys());
    this.eventListLabels = Array.from(this.eventMap.values()).map(e => e.label);
  }

  private setInitialEvent(): void {
    const firstRound = this.availableRaces?.find(r => r.round === 1);
    if (firstRound?.eventId) {
      this.inputForm.controls['eventId'].setValue(firstRound.eventId);
    } else if (this.eventIds.length > 0) {
      this.inputForm.controls['eventId'].setValue(this.eventIds[0]);
    }
  }

  private registerSubscriptions(): void {
    this.subscriptions.push(
        this.inputForm.controls.raceId.valueChanges.subscribe(raceId => {
          this.updateSelectedRoundResult();
          this.initState();
          this.onExplore();
        }),
        this.inputForm.controls.eventId.valueChanges.subscribe(eventId => {
          this.inputForm.controls.raceId.setValue(this.eventMap.get(eventId)?.raceIds[0] ?? null);
        })
    );
    this.subscriptions.push(
        this.metaSubject.pipe(
            switchMap(raceId => {
              this.currentRoundResultsMeta = undefined;
              return this.leagueResultsService.getLeagueRoundResultsMeta(this.leagueId!, raceId)
            })
        ).subscribe(meta => {
          this.currentRoundResultsMeta = meta ?? undefined;
          this.initState();
        })
    );
  }

  updateSelectedRoundResult(): void {
    const raceId = this.inputForm.controls['raceId'].value;
    if (raceId != null) {
      this.metaSubject.next(raceId);
    } else {
      this.currentRoundResultsMeta = undefined;
    }
  }

  private initState() {
    const raceId = this.inputForm.controls['raceId'].value;
    const selectedRace = this.availableRaces?.find(r => r.raceId === raceId);
    this.isTeam = selectedRace?.isTeam ?? false;

    this.buildFilteringParams();
    this.rankingContext = selectedRace?.hasRoundResults ||
    this.currentRoundResultsMeta?.availableRankingContext?.includes(LeagueRoundResultContext.ROUND)
        ? LeagueRoundResultContext.ROUND : LeagueRoundResultContext.RACE;
    if (raceId) {
      this.filter = new LeagueRoundResultsFilter(
          raceId, undefined, undefined,
          undefined,
          undefined,
          this.rankingContext
      );
    }
  }

  private buildFilteringParams(filter?: LeagueRoundResultsFilter) {
    this.compositionMap = new Map();
    if (this.currentRoundResultsMeta?.availableTeamCompositions) {
      this.currentRoundResultsMeta.availableTeamCompositions.forEach((tc, index) => {
        this.compositionMap.set(index, tc);
      });
    }

    this.filterGroups = [
      ...(!this.isTeam ? [
        new FilterGroup('gender', 'gender', 'app.common.gender',
            FilterGroupType.SINGLE_SELECT, false, [
          new FilterItem(Gender.MALE, undefined, 'app.common.gender.1', filter?.gender === Gender.MALE),
          new FilterItem(Gender.FEMALE, undefined, 'app.common.gender.2', filter?.gender === Gender.FEMALE),
        ], false, true)
      ] : []),
      ...(!this.isTeam && this.categoryItems.length ? [
        new FilterGroup('category', 'category', 'app.common.category',
            FilterGroupType.SINGLE_SELECT, false,
            this.categoryItems
                .filter(ci => !filter?.gender || filter.gender === ci.gender)
                .map(ci => new FilterItem(ci.id, ci.code, undefined, filter?.categoryItemId === ci.id)),
            false, true)
      ] : []),
      ...(this.isTeam && this.currentRoundResultsMeta?.availableTeamCompositions?.length ? [
        new FilterGroup('teamComposition', 'teamComposition', 'league.results.team.composition',
            FilterGroupType.SINGLE_SELECT, false,
            this.currentRoundResultsMeta.availableTeamCompositions.map((tc, index) => new FilterItem(
                index,
                this.buildCompositionLabel(tc),
                undefined,
                filter?.teamComposition?.maleCount === tc.maleCount && filter?.teamComposition?.femaleCount === tc.femaleCount
            )), false, true
        )
      ] : []),
    ];
  }

  onFilterChange(result: Record<string, any>): void {
    const raceId = this.inputForm.controls.raceId.value;
    if (raceId) {
      this.filter = new LeagueRoundResultsFilter(
          raceId,
          !this.isTeam ? result['gender'] : undefined,
          !this.isTeam ? result['category'] : undefined,
          !this.isTeam ? result['searchTerm'] : undefined,
          this.isTeam ? this.compositionMap.get(result['teamComposition']) : undefined,
          this.rankingContext
      );
      this.results = []
      this.onExplore();
    }
  }

  toggleCountStatus(result: LeagueCompetitorRoundResult): void {
    const newStatus = result.countStatus === RoundCountStatus.NOT_INCLUDED
        ? RoundCountStatus.INCLUDED
        : RoundCountStatus.NOT_INCLUDED;

    this.leagueResultsService.toggleRoundResultCountStatus(
        this.leagueId!, result.raceId!, result.competitorId!, newStatus
    ).subscribe({
      next: () => {
        this.onExplore();
        this.updateMeta(result.raceId!);
      },
      error: () => {}
    });
  }

  applyToPublic(): void {
    const raceId = this.inputForm.controls.raceId.value;
    if (raceId === null || this.leagueId == null || this.currentRoundResultsMeta?.hasDuplicates || !this.currentRoundResultsMeta?.hasResults) return;
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("league.roundResults.apply.public.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("league.roundResults.apply.public.dialog.text");

    modalRef.result.then((_) => {
      this.loaderService.startLoading();
      this.leagueResultsService.applyRoundResultsToPublic(this.leagueId!, raceId).subscribe({
        next: (success: boolean) => {
          this.loaderService.dismissLoading();
          if (success) {
            this.onExplore();
            this.updateMeta(raceId);
          }
          this.toastService.showToast(success ?
                  TOAST_STATE.success : TOAST_STATE.danger,
              this.translocoService.translate(success ?
                  "league.roundResults.apply.public.success" : "league.roundResults.apply.public.error"));
        },
        error: () => this.loaderService.dismissLoading()
      });
    }, (_) => {
      /// ignore
    });
  }

  updateMeta(raceId: number): void {
    this.metaSubject.next(raceId);
  }

  protected override afterSetItems(items: LeagueCompetitorRoundResult[]): void {
    this.results = items;
  }

  protected fetchPage(page: number): Observable<LeagueCompetitorRoundResult[]> {
    let pagedFilter: PagedFilter<LeagueRoundResultsFilter> =
        new PagedFilter(new Pages(this.PAGE_SIZE, this.page),
        this.filter);
    return this.leagueResultsService.getCompetitorRoundResults(this.leagueId!, pagedFilter);
  }

  onChangePoints(result: LeagueCompetitorRoundResult, type: 'base' | 'bonus' | 'penalty', event: Event) {
    if (!result.points) {
      result.points = new LeaguePoints();
    }
    const raw = (event.target as HTMLInputElement).value;
    const value = raw === '' ? undefined : +raw;

    switch (type) {
      case "base": result.points.basePoints = value; break;
      case "bonus": result.points.bonus = value; break;
      case "penalty": result.points.penalty = value; break;
    }
  }

  selectRankingContext(rankingContext: LeagueRoundResultContext) {
    this.filter.context = rankingContext;
    this.onExplore();
  }

  enableEditing() {
    this.editEnabled = true;
    this.componentType = LeagueRoundResultsComponentType.EDIT;
    this.originalResults = new Map(this.results.map(r => [r.competitorId!, structuredClone(r)]));
  }

  recalculate() {
    //todo
  }

  cancel() {
    this.editEnabled = false;
    this.componentType = LeagueRoundResultsComponentType.VIEW;
    this.results = Array.from(this.originalResults.values());
    this.originalResults = new Map();
  }

  savePoints(): void {
    const raceId = this.inputForm.controls.raceId.value;
    if (raceId == null || this.leagueId == null || this.results.length === 0) return;
    if (!this.results.some(r => r.points?.basePoints != null)) return;

    const changed = this.results.filter(r => {
      const original = this.originalResults.get(r.competitorId!);
      if (!original) return true;
      return r.points?.basePoints !== original.points?.basePoints
          || r.points?.bonus !== original.points?.bonus
          || r.points?.penalty !== original.points?.penalty
          || r.countStatus !== original.countStatus;
    });

    if (changed.length === 0) return;

    this.loaderService.startLoading();
    this.leagueResultsService.saveRoundResults(this.leagueId!, raceId, changed).subscribe({
      next: () => {
        this.cancel();
        this.loaderService.dismissLoading();
        this.onExplore();
        this.updateMeta(raceId);
      },
      error: () => this.loaderService.dismissLoading()
    });
  }

  private buildCompositionLabel(tc: TeamComposition): string {
    const parts = [];
    if (tc.maleCount) parts.push(`${tc.maleCount}${this.translocoService.translate("app.common.gender.short.1")}`);
    if (tc.femaleCount) parts.push(`${tc.femaleCount}${this.translocoService.translate("app.common.gender.short.2")}`);
    return parts.join(' + ');
  }

  protected readonly RoundCountStatus = RoundCountStatus;
  protected readonly LeagueRoundResultsComponentType = LeagueRoundResultsComponentType;
  protected readonly LeagueRoundResultContext = LeagueRoundResultContext;
  protected readonly PublicInclusionStatus = PublicInclusionStatus;

}

enum LeagueRoundResultsComponentType {
  EDIT = 1,
  VIEW = 2
}
